import {
  Badge,
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  Icon,
  Input,
  Switch,
} from "@elaniintc/goes-design-system";
import { usePaymentNodeContext } from "./context";
import { VariableSelector } from "./VariableSelector";
import { formatNumberInput } from "@/utils/number";
import type { NodeVariable } from "../types";

const Content = ({ variables }: { variables: NodeVariable[] }) => {
  const {
    payment_title,
    setPaymentTitle,
    description,
    setDescription,
    canEximp,
    setCanEximp,
    express,
    setExpress,
    paymentItems,
    isItemValid,
    updatePaymentItem,
    removePaymentItem,
    addPaymentItem,
    canAddNewItem,
    hasPaymentTitleChange,
  } = usePaymentNodeContext();
  return (
    <div className="py-3 flex flex-col gap-3">
      {/* Información básica */}
      <div className="w-full pt-3 pb-6 px-4 border-b-1 border-neutral-quiet flex flex-col gap-4">
        <Heading variant="h6">Información básica</Heading>

        {/* Título del pago */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-body-loud">
            Título del pago<span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={payment_title}
            onChange={(e) => setPaymentTitle(e.target.value)}
            placeholder="Escribe el título del pago"
            className={`w-full px-3 py-2 border rounded-lg text-body-loud placeholder:text-body-quiet focus:outline-none focus:border-contextual-action ${
              hasPaymentTitleChange && !payment_title.trim()
                ? "border-danger"
                : "border-neutral-quiet"
            }`}
          />
          {hasPaymentTitleChange && !payment_title.trim() && (
            <span className="text-xs text-danger">
              El título del pago es obligatorio
            </span>
          )}
        </div>

        {/* Descripción del pago */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-body-loud">
              Descripción del pago
            </label>
            <span className="text-xs text-body-quiet">Opcional</span>
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Escribe una descripción para el pago"
            rows={4}
            className="w-full px-3 py-2 border border-neutral-quiet rounded-lg text-body-loud placeholder:text-body-quiet focus:outline-none focus:border-contextual-action resize-none"
          />
        </div>

        {/* Posibilidad para eximir pago */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-body-loud">
            Posibilidad para eximir pago
            <span className="text-danger">*</span>
          </label>
          <div className="flex items-center gap-3">
            <Switch
              checked={!canEximp}
              onCheckedChange={(checked) => setCanEximp(!checked)}
            />
            <span className="text-sm text-body">
              {canEximp
                ? "No, el pago es obligatorio."
                : "Sí, el pago puede ser eximido."}
            </span>
          </div>
        </div>

        {/* Marcar como express */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-body-loud">
            Marcar como express
            <span className="text-danger">*</span>
          </label>
          <div className="flex items-center gap-3">
            <Switch
              checked={express}
              onCheckedChange={(checked) => setExpress(checked)}
            />
            <span className="text-sm text-body">
              {express
                ? "Sí, este pago marca el trámite como express"
                : "No, este pago no marca el trámite como express"}
            </span>
          </div>
        </div>
      </div>

      {/* Detalle de pago */}
      <div className="w-full pt-3 pb-6 px-4 flex flex-col gap-4">
        <Heading variant="h6">Detalle de pago</Heading>

        {/* Items de pago dinámicos */}
        <div className="space-y-2">
          {paymentItems.map((item, index) => {
            const validation = isItemValid(item);

            return (
              <div key={item.id} className="flex items-center gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-body-loud leading-l">
                      Concepto y valor {index + 1}
                      <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="flex flex-col">
                    {/* Concepto Input */}
                    <GroupTextField label="" className="flex-1">
                      <GroupTextFieldRow className="overflow-x-hidden">
                        <Button
                          variant="quiet"
                          group="up-left"
                          onClick={() =>
                            updatePaymentItem(item.id, {
                              conceptType:
                                item.conceptType === "input"
                                  ? "variable"
                                  : "input",
                              concept: "",
                              conceptNode: undefined,
                              conceptField: undefined,
                              conceptOutput: undefined,
                              conceptGlobalType: undefined,
                            })
                          }
                        >
                          {item.conceptType === "input" ? "inp" : "var"}
                        </Button>
                        {item.conceptType === "input" ? (
                          <Input
                            className="w-full"
                            placeholder="Escribe el concepto"
                            maxLength={255}
                            value={item.concept}
                            onChange={(e) =>
                              updatePaymentItem(item.id, {
                                concept: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <VariableSelector
                            variables={variables}
                            type="string"
                            group="up-right"
                            selectedValue={{
                              field_id: item.conceptField,
                              output_name: item.conceptOutput,
                              node_id: item.conceptNode,
                              global_type: item.conceptGlobalType?.type,
                              value: item.conceptGlobalType?.value,
                            }}
                            onUpdateElement={({
                              node_id,
                              field_id,
                              output_name,
                              global_type,
                              value,
                            }) => {
                              updatePaymentItem(item.id, {
                                conceptNode: node_id,
                                conceptField: field_id,
                                conceptOutput: output_name,
                                conceptType: "variable",
                                concept: "",
                                ...(global_type
                                  ? value && {
                                      conceptGlobalType: {
                                        value,
                                        type: global_type,
                                      },
                                    }
                                  : {
                                      conceptGlobalType: undefined,
                                    }),
                              });
                            }}
                            placeholder="Seleccionar variable para concepto"
                          />
                        )}
                      </GroupTextFieldRow>
                      {/* Valor Input */}
                      <GroupTextFieldRow className="overflow-x-hidden">
                        <Button
                          variant="quiet"
                          group="down-left"
                          onClick={() =>
                            updatePaymentItem(item.id, {
                              amountType:
                                item.amountType === "input"
                                  ? "variable"
                                  : "input",
                              amount: "",
                              amountNode: undefined,
                              amountField: undefined,
                              amountOutput: undefined,
                            })
                          }
                        >
                          {item.amountType === "input" ? "inp" : "var"}
                        </Button>
                        {item.amountType === "input" ? (
                          <div className="relative flex-1">
                            <Input
                              className="w-full pr-16"
                              placeholder="$0.00"
                              group="down-right"
                              value={item.amount}
                              onChange={(e) => {
                                const formattedValue = formatNumberInput(
                                  e.target.value,
                                  true,
                                  false,
                                  2,
                                );
                                updatePaymentItem(item.id, {
                                  amount: formattedValue,
                                });
                              }}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <Badge variant="neutral" size="sm">
                                USD
                              </Badge>
                            </div>
                          </div>
                        ) : (
                          <VariableSelector
                            variables={variables}
                            type="number"
                            group="down-right"
                            selectedValue={{
                              field_id: item.amountField,
                              output_name: item.amountOutput,
                              node_id: item.amountNode,
                              global_type: item.amountGlobalType?.type,
                              value: item.amountGlobalType?.value,
                            }}
                            onUpdateElement={({
                              node_id,
                              field_id,
                              output_name,
                              global_type,
                              value,
                            }) => {
                              updatePaymentItem(item.id, {
                                amountNode: node_id,
                                amountField: field_id,
                                amountOutput: output_name,
                                amountType: "variable",
                                amount: "",
                                ...(global_type
                                  ? value && {
                                      amountGlobalType: {
                                        value,
                                        type: global_type,
                                      },
                                    }
                                  : {
                                      amountGlobalType: undefined,
                                    }),
                              });
                            }}
                            placeholder="Seleccionar variable para valor"
                          />
                        )}
                      </GroupTextFieldRow>
                    </GroupTextField>
                    {/* Mensajes de error - temporalmente deshabilitados */}
                    {item.hasChange && !validation.hasValidConcept && (
                      <span className="text-xs text-danger mt-1">
                        {item.conceptType === "input"
                          ? "El concepto es obligatorio"
                          : "Debe seleccionar una variable para el concepto"}
                      </span>
                    )}
                    {item.hasChange && !validation.hasValidAmount && (
                      <span className="text-xs text-danger mt-1">
                        {item.amountType === "input"
                          ? "El valor debe ser un número mayor a 0"
                          : "Debe seleccionar una variable para el valor"}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between gap-3"></div>
                  </div>
                </div>
                {index > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePaymentItem(item.id)}
                    aria-label="Eliminar"
                  >
                    <Icon name="times" type="far" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Botón para añadir ítem */}
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            size="sm"
            onClick={addPaymentItem}
            disabled={!canAddNewItem()}
          >
            Añadir ítem <Icon name="plus" type="far" />
          </Button>
          {paymentItems.length >= 10 && (
            <span className="text-xs text-body-quiet self-center">
              Máximo 10 ítems permitidos
            </span>
          )}
          {paymentItems.length < 10 && !canAddNewItem() && (
            <span className="text-xs text-body-quiet self-center">
              Complete el ítem actual para agregar otro
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Content;
